import "./ReminderSelector.scss";

import { Alert, Button, Dropdown, Flex, Input, Text } from "@fluentui/react-northstar";
import { FieldDescriptors } from "@shopify/react-form-state";
import { TeamsSurveyReminder } from "@teams/types";
import moment from "moment";
import React, { useEffect, useState } from "react";

import { PollyIcon } from "../PollyIcon";

type Props = {
  reminder: FieldDescriptors<TeamsSurveyReminder>;
  onRemoveReminder: (reminderId: string) => void;
};

type TimeUnit = "minutes" | "hours" | "days";

const minutesInHour = 60;
const minutesInDay = 60 * 24;

export const ReminderRow: React.FC<Props> = ({ reminder, onRemoveReminder }) => {
  let initialNumberPart = moment.duration(reminder.duration.value).as("minutes");
  let initialTimeUnit: TimeUnit = "minutes";

  if (initialNumberPart >= minutesInDay && initialNumberPart % minutesInDay === 0) {
    initialNumberPart = Math.floor(initialNumberPart / minutesInDay);
    initialTimeUnit = "days";
  } else if (initialNumberPart >= minutesInHour && initialNumberPart % minutesInHour === 0) {
    initialNumberPart = Math.floor(initialNumberPart / minutesInHour);
    initialTimeUnit = "hours";
  }

  const [numberPart, setNumberPart] = useState<number | undefined>(initialNumberPart);
  const [timeUnit, setTimeUnit] = useState<TimeUnit>(initialTimeUnit);

  useEffect(() => {
    let minutes = numberPart;
    let value = `PT${minutes}M`;
    if (timeUnit === "days") {
      value = `P${numberPart}D`;
    } else if (timeUnit === "hours") {
      value = `PT${numberPart}H`;
    }

    if (value !== reminder.duration.value) {
      reminder.duration.onChange(value);
    }
  }, [reminder, numberPart, timeUnit]);

  return (
    <>
      <Flex gap="gap.small" vAlign="center" className="reminder-row">
        <Text content="Notify users" />
        <div style={{ width: "75px" }}>
          <Input
            type="number"
            fluid
            defaultValue={numberPart?.toString()}
            onBlur={e => {
              const newValue = (e.currentTarget as HTMLInputElement).value;
              let value = parseInt(newValue);

              if (isNaN(value)) {
                setNumberPart(undefined);
              } else {
                setNumberPart(value);
              }
            }}
          />
        </div>
        <Dropdown
          items={["minutes", "hours", "days"]}
          fluid
          value={timeUnit}
          onChange={(_, props) => {
            if (props && props.value) {
              setTimeUnit(props.value as TimeUnit);
              switch (props.value as TimeUnit) {
                case "hours":
                case "days":
                  setNumberPart(1);
                  break;
                case "minutes":
                  setNumberPart(30);
                  break;
              }
            }
          }}
        />
        <Text content="before&nbsp;close" />
        <Button
          className="trash-icon-button"
          data-tname="remove-reminder"
          iconOnly
          title="Remove"
          onClick={ev => {
            ev.preventDefault();
            onRemoveReminder(reminder.id?.value as string);
          }}
        >
          <PollyIcon icon="trash-icon" />
        </Button>
      </Flex>

      {reminder.duration.error && (
        <Alert danger content={reminder.duration.error} attached="bottom" />
      )}
    </>
  );
};
